import { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import * as Yup from 'yup';
import {
  APPROVAL_FLOW_OPTIONS,
  CHAIN_FLOW_OPTIONS,
  FLOW_TYPES,
  CAMPAIGN_WIZARD_STEPS,
  HIRE_WIZARD_STEPS,
  REQUEST_TYPES,
} from './constants';
import {
  getPageConfig,
  VIEW_TYPES,
} from './viewConfig';
import { formatRequestLocationLabel } from './recruitmentRequestMappers';
import { basicInformationValidationSchema } from '../job/creation/assets/jobCreationUtill';
import { mapApprovalChainToFormValues } from './approvalChainMappers';
import { MOCK_PROMOTION_CHANNELS } from './service-data';
import { formateLocalNumber, getCurrencySign } from '../../utils/helper';
import {
  useGetAuthorityReferenceDataQuery,
  useGetAuthorityRequestStatsQuery,
  useGetAuthorityRequestsQuery,
} from './authorityToRecruitApiSlice';

export const buildDefaultChannelChecks = () =>
  MOCK_PROMOTION_CHANNELS.reduce((acc, ch) => ({ ...acc, [ch.id]: false }), {});

export const getSelectedChannelIds = (channelChecks = {}) =>
  Object.entries(channelChecks)
    .filter(([, selected]) => selected)
    .map(([id]) => id);

export const getRadioOptionValue = (value) =>
  value && typeof value === 'object' ? value.value : value;

export const getApprovalFlowType = (value) => {
  const raw = getRadioOptionValue(value);
  if (raw === FLOW_TYPES.PARALLEL || raw === FLOW_TYPES.SEQUENTIAL) return raw;
  const match = APPROVAL_FLOW_OPTIONS.find((o) => o.label === raw || o.value === raw);
  return match?.value ?? FLOW_TYPES.SEQUENTIAL;
};

export const normalizeWizardPayload = (values) => ({
  ...values,
  approval: {
    ...values.approval,
    flowType: getApprovalFlowType(values.approval?.flowType),
  },
});

export const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export const formatRelativeTime = (date) => (date ? moment(date).fromNow() : '');

export const getRequesterDisplayName = (request, { viewType, currentUser } = {}) => {
  const requested = request?.requestedBy;
  const mappedName = requested?.name?.trim();

  if (mappedName && mappedName !== 'Unknown') {
    return mappedName;
  }

  if (viewType === VIEW_TYPES.REQUESTER && currentUser) {
    const authName = [currentUser.firstName, currentUser.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (authName) return authName;
    return 'You';
  }

  return mappedName || 'Unknown';
};

export const formatMetadataLine = (items = []) => items.filter(Boolean).join(' • ');

const isCompetitiveSalary = (value) =>
  value === true || value === 1 || value === 'yes' || value === '1';

const mapTemplateSalaryPeriodToHire = (period) => {
  if (!period) return 'year';
  if (period === 'Annum' || period === 'annual') return 'year';
  return period;
};

const toHireDepartmentOption = (template = {}) => {
  const department = template.department;
  if (department && typeof department === 'object') {
    const value = department.value ?? department.id ?? template.departmentId;
    const label =
      department.label ||
      department.title ||
      department.name ||
      template.departmentName;
    if (!value && !label) return undefined;
    return { value, label };
  }
  if (template.departmentId) {
    return {
      value: template.departmentId,
      label: template.departmentName || template.department || '',
    };
  }
  return undefined;
};

const toHireLocationOption = (template = {}, plan = {}) => {
  const location =
    template.centralLocation ||
    plan.centralLocation ||
    plan.location ||
    template.location;

  if (!location || typeof location !== 'object') return undefined;

  const id =
    location.id ??
    location.locationId ??
    location.value ??
    template.centralLocationId;

  if (!id && !location.title && !location.label) return undefined;

  return {
    ...location,
    id,
    value: id,
    title: location.title || location.label || location.name || '',
  };
};

/**
 * Maps a job-template API response onto ATR hire form fields that overlap.
 * Only returns defined values so empty template fields do not wipe user input.
 */
export const buildHireFieldsFromJobTemplate = (template = {}) => {
  if (!template || typeof template !== 'object') return {};

  const plan = Array.isArray(template.jobHiringPlan)
    ? template.jobHiringPlan[0] || {}
    : template.jobHiringPlan && typeof template.jobHiringPlan === 'object'
      ? template.jobHiringPlan
      : {};

  const department = toHireDepartmentOption(template);
  const location = toHireLocationOption(template, plan);
  const employmentType = template.employmentType || plan.employmentType;
  const jobType = plan.jobType || template.jobType;
  // Use positions count only — template.position is job level (e.g. ENTRY_LEVEL), not headcount.
  const rawPositions = plan.positions ?? template.positions;
  const positionsNumber = Number(rawPositions);
  const positions =
    rawPositions != null &&
    rawPositions !== '' &&
    Number.isFinite(positionsNumber) &&
    positionsNumber >= 1
      ? positionsNumber
      : undefined;
  const salaryFrom = plan.salaryFrom ?? template.salaryFrom;
  const salaryTo = plan.salaryTo ?? template.salaryTo;
  const salaryCurrency =
    plan.salaryCurrency || template.salaryCurrency || undefined;
  const salaryPeriod = mapTemplateSalaryPeriodToHire(
    plan.salaryPeriod || template.salaryPeriod || template.salaryPer,
  );
  const isCompetitive = isCompetitiveSalary(
    plan.isCompetitive ?? template.isCompetitive,
  );
  const hasSalary =
    (salaryFrom != null && salaryFrom !== '') ||
    (salaryTo != null && salaryTo !== '');

  const fields = {
    jobTitle: template.title || template.jobTitle || undefined,
  };

  if (department) fields.department = department;
  if (location) fields.location = location;
  if (employmentType) fields.employmentType = employmentType;
  if (jobType) fields.jobType = jobType;
  if (positions != null) fields.positions = positions;

  if (isCompetitive) {
    fields.isCompetitive = true;
    fields.salaryFrom = '';
    fields.salaryTo = '';
  } else if (hasSalary) {
    fields.isCompetitive = false;
    if (salaryFrom != null && salaryFrom !== '') fields.salaryFrom = salaryFrom;
    if (salaryTo != null && salaryTo !== '') fields.salaryTo = salaryTo;
  }

  if (salaryCurrency) fields.salaryCurrency = salaryCurrency;
  if (salaryPeriod) fields.salaryPeriod = salaryPeriod;

  return Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value !== undefined),
  );
};

const replaceCurrencyCodesInRange = (value = '') =>
  String(value).replace(
    /(GBP|USD|EUR|AED|AUD|INR|MYR|ZAR)(?=\d)/g,
    (code) => getCurrencySign(code),
  );

export const formatHireSalarySummary = (hire = {}) => {
  if (isCompetitiveSalary(hire.isCompetitive)) return 'Competitive';

  const currency = getCurrencySign(hire.salaryCurrency || 'GBP');
  const period = hire.salaryPeriod ? ` / ${hire.salaryPeriod}` : '';
  const from = hire.salaryFrom;
  const to = hire.salaryTo;

  if (from != null && from !== '' && to != null && to !== '') {
    return `${currency}${formateLocalNumber(from)} - ${currency}${formateLocalNumber(to)}${period}`.trim();
  }
  if (from != null && from !== '') return `${currency}${formateLocalNumber(from)}${period}`.trim();
  if (to != null && to !== '') return `${currency}${formateLocalNumber(to)}${period}`.trim();
  return '';
};

export const getRequestSalaryLabel = (request = {}) => {
  const fromFields = formatHireSalarySummary(request);
  if (fromFields) return fromFields;
  if (request.salaryRange) return replaceCurrencyCodesInRange(request.salaryRange);
  return '';
};

const getRequestSalaryTooltip = (request = {}) => {
  if (isCompetitiveSalary(request.isCompetitive)) return 'Salary: Competitive';

  const currency = getCurrencySign(request.salaryCurrency || 'GBP');
  const period = request.salaryPeriod ? ` / ${request.salaryPeriod}` : '';
  const from = request.salaryFrom;
  const to = request.salaryTo;

  if (from != null && from !== '' && to != null && to !== '') {
    return `Salary from: ${currency}${formateLocalNumber(from)} - Salary to: ${currency}${formateLocalNumber(to)}${period}`;
  }
  if (from != null && from !== '') {
    return `Salary from: ${currency}${formateLocalNumber(from)}${period}`;
  }
  if (to != null && to !== '') {
    return `Salary to: ${currency}${formateLocalNumber(to)}${period}`;
  }

  const label = getRequestSalaryLabel(request);
  return label ? `Salary: ${label}` : '';
};

const toMetaItem = (label, tooltip) => (label ? { label, tooltip: tooltip || label } : null);

export const getFlowTypeTooltip = (flowType) => {
  const option = APPROVAL_FLOW_OPTIONS.find((o) => o.value === flowType);
  if (!option) return flowType ? `Approval flow: ${flowType}` : '';
  return `Approval flow: ${option.label} — ${option.description}`;
};

export const getRequestMetaItems = (request) => {
  if (!request) return [];
  if (request.type === REQUEST_TYPES.CAMPAIGN) {
    return [
      toMetaItem(request.jobTitle, request.jobTitle ? `Job: ${request.jobTitle}` : null),
      toMetaItem(request.budget, request.budget ? `Budget: ${request.budget}` : null),
      toMetaItem(
        request.durationDays ? `${request.durationDays} days` : null,
        request.durationDays ? `Duration: ${request.durationDays} days` : null,
      ),
      toMetaItem(
        request.channels?.length ? `${request.channels.length} channel(s)` : null,
        request.channels?.length ? `Channels: ${request.channels.length} channel(s)` : null,
      ),
    ].filter(Boolean);
  }

  const location = formatRequestLocationLabel(request);
  const positionsLabel = request.positions
    ? `${request.positions} position${request.positions === 1 ? '' : 's'}`
    : null;
  const salaryLabel = getRequestSalaryLabel(request) || null;

  return [
    toMetaItem(
      request.department,
      request.department ? `Department: ${request.department}` : null,
    ),
    toMetaItem(location, location ? `Location: ${location}` : null),
    toMetaItem(
      positionsLabel,
      request.positions ? `Positions: ${request.positions}` : null,
    ),
    toMetaItem(salaryLabel, getRequestSalaryTooltip(request) || null),
  ].filter(Boolean);
};

export const formatApprovalProgress = (approved, total) =>
  total ? `${approved}/${total} approved` : '';

export const formatChainRolesSummary = (approvers = []) =>
  approvers.map((a) => a.role).filter(Boolean).join(' → ');

export const getWizardInitialValues = (requestType = REQUEST_TYPES.HIRE) => ({
  requestType,
  hire: {
    jobTitle: '',
    jobTemplate: null,
    department: undefined,
    location: undefined,
    employmentType: undefined,
    jobType: 'PERMANENT',
    positions: 1,
    salaryFrom: '',
    salaryTo: '',
    salaryCurrency: 'GBP',
    salaryPeriod: 'year',
    isCompetitive: false,
    reason: '',
    justification: '',
  },
  campaign: {
    job: undefined,
    title: '',
    reason: '',
    justification: '',
  },
  approval: {
    templateId: '',
    flowType: APPROVAL_FLOW_OPTIONS[0].label,
    approvers: [],
    _approverPicker: null,
  },
});

const mapRequestApproversToFormValues = (approvers = []) =>
  approvers
    .filter((a) => a.role !== 'Requester')
    .map((a) => ({
      value: a.userId ?? a.accountId ?? a.id,
      label: a.name,
      name: a.name,
      role: a.role,
      jobTitle: a.jobTitle,
      email: a.email,
      companyRoles: a.companyRoles,
      accountCompanyRoles: a.accountCompanyRoles,
    }));

const mapRequestApprovalToFormValues = (request = {}) => ({
  templateId: '',
  flowType:
    APPROVAL_FLOW_OPTIONS.find((o) => o.value === request.flowType)?.label ||
    APPROVAL_FLOW_OPTIONS[0].label,
  approvers: mapRequestApproversToFormValues(request.approvers),
  _approverPicker: null,
});

export const mapRecruitmentRequestToAmendValues = (request) => {
  if (!request) return getWizardInitialValues();

  if (request.type === REQUEST_TYPES.CAMPAIGN) {
    return {
      requestType: REQUEST_TYPES.CAMPAIGN,
      campaign: {
        job:
          request.job ||
          (request.jobId
            ? { value: request.jobId, label: request.jobTitle || request.title }
            : undefined),
        title: request.title || '',
        reason: request.reason || '',
        justification: request.justification || '',
      },
      approval: mapRequestApprovalToFormValues(request),
    };
  }

  const department = request.departmentRecord
    ? {
        value: request.departmentRecord.id ?? request.departmentId,
        label:
          request.departmentRecord.name ||
          request.departmentRecord.title ||
          request.departmentRecord.label ||
          request.department,
      }
    : request.departmentId
      ? { value: request.departmentId, label: request.department }
      : undefined;

  const location = request.centralLocation
    ? {
        ...request.centralLocation,
        id:
          request.centralLocation.id ??
          request.centralLocationId ??
          request.centralLocation.locationId,
      }
    : undefined;

  const hasSalary = request.salaryFrom != null || request.salaryTo != null;

  const jobTemplateId = request.tplId ?? request.jobTemplateId ?? request.jobTemplate?.value ?? request.jobTemplate?.id;
  const jobTitle = request.title || request.jobTitle || '';
  const jobTemplate = jobTemplateId
    ? {
        value: jobTemplateId,
        label:
          request.jobTemplate?.label ||
          request.jobTemplate?.title ||
          request.jobTemplateTitle ||
          jobTitle,
      }
    : null;

  return {
    requestType: REQUEST_TYPES.HIRE,
    hire: {
      jobTitle,
      jobTemplate,
      department,
      location,
      employmentType: request.employmentType || undefined,
      jobType: request.jobType || 'PERMANENT',
      positions: (() => {
        const n = Number(request.positions);
        return Number.isFinite(n) && n >= 1 ? n : 1;
      })(),
      salaryFrom: request.salaryFrom ?? '',
      salaryTo: request.salaryTo ?? '',
      salaryCurrency: request.salaryCurrency || 'GBP',
      salaryPeriod: request.salaryPeriod || 'year',
      isCompetitive: hasSalary ? false : isCompetitiveSalary(request.isCompetitive),
      reason: request.reason || '',
      justification: request.justification || '',
    },
    approval: mapRequestApprovalToFormValues(request),
  };
};

// When "No salary / competitive" is checked, salary fields are cleared and not required.
const hireSalaryFromSchema = Yup.number()
  .nullable()
  .transform((value, original) => (original === '' ? null : value))
  .when('isCompetitive', {
    is: (val) => val === true || val === 1 || val === 'yes',
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema
        .min(0, 'Salary cannot be negative')
        .positive('Salary must be a positive number')
        .test(
          'max-2-decimals',
          'Only up to 2 decimal places are allowed',
          (value) => {
            if (value == null) return true;
            return /^\d+(\.\d{1,2})?$/.test(String(value));
          },
        )
        .required('Required'),
  });

const hireSalaryToSchema = Yup.number()
  .nullable()
  .transform((value, original) => (original === '' ? null : value))
  .when('isCompetitive', {
    is: (val) => val === true || val === 1 || val === 'yes',
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema
        .min(0, 'Salary cannot be negative')
        .positive('Salary must be a positive number')
        .test(
          'max-2-decimals',
          'Only up to 2 decimal places are allowed',
          (value) => {
            if (value == null) return true;
            return /^\d+(\.\d{1,2})?$/.test(String(value));
          },
        )
        .required('Required')
        .when('salaryFrom', (salaryFrom, innerSchema) =>
          salaryFrom != null
            ? innerSchema.min(
                salaryFrom,
                `Must be equal or greater than ${salaryFrom}`,
              )
            : innerSchema,
        ),
  });

const hireRoleDetailsSchema = Yup.object({
  hire: Yup.object({
    jobTitle: Yup.string().trim().required('Required'),
    employmentType: Yup.string().required('Required'),
    jobType: Yup.string().required('Required'),
    department: Yup.mixed()
      .nullable()
      .test('selected', 'Required', (value) => {
        if (!value) return true;
        if (typeof value === 'string') return Boolean(value.trim());
        return Boolean(value?.value ?? value?.id);
      }),
    reason: Yup.string().trim().required('Required'),
    location: basicInformationValidationSchema.location,
    positions: Yup.number()
      .transform((value, original) => {
        if (original === '' || original == null) return 1;
        const n = typeof original === 'number' ? original : Number(original);
        return Number.isFinite(n) ? n : Number.NaN;
      })
      .typeError('Must be a number')
      .required('Required')
      .integer('Must be a whole number')
      .min(1, 'Must be at least 1'),
    salaryCurrency: Yup.string().when('isCompetitive', {
      is: (val) => val === true || val === 1 || val === 'yes',
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required('Required'),
    }),
    salaryPeriod: Yup.string().when('isCompetitive', {
      is: (val) => val === true || val === 1 || val === 'yes',
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required('Required'),
    }),
    salaryFrom: hireSalaryFromSchema,
    salaryTo: hireSalaryToSchema,
    isCompetitive: Yup.boolean(),
    justification: Yup.string(),
  }),
});

const campaignDetailsSchema = Yup.object({
  campaign: Yup.object({
    job: Yup.mixed()
      .required('Required')
      .test('selected', 'Required', (value) => {
        if (!value) return false;
        if (typeof value === 'string') return Boolean(value.trim());
        return Boolean(value?.value ?? value?.id);
      }),
    title: Yup.string().trim().required('Required'),
    reason: Yup.string().trim().required('Required'),
    justification: Yup.string(),
  }),
});

const approvalChainSchema = Yup.object({
  approval: Yup.object({
    flowType: Yup.mixed()
      .required('Required')
      .test('selected', 'Required', (value) => {
        if (value == null || value === '') return false;
        const raw = getRadioOptionValue(value);
        return APPROVAL_FLOW_OPTIONS.some((o) => o.label === raw || o.value === raw);
      }),
    approvers: Yup.array()
      .min(1, 'Add at least one approver')
      .required('Add at least one approver'),
  }),
});

const STEP_SCHEMAS = {
  role_details: hireRoleDetailsSchema,
  campaign_details: campaignDetailsSchema,
  approval_chain: approvalChainSchema,
  review: null,
};

export const getWizardValidationSchema = (stepKey) => STEP_SCHEMAS[stepKey] || null;

export const getCreateChainInitialValues = () => ({
  id: null,
  name: '',
  type: REQUEST_TYPES.HIRE,
  description: '',
  flowType: CHAIN_FLOW_OPTIONS[0].label,
  approvers: [],
  _approverPicker: null,
});

export const getChainFormValuesFromChain = (chain) => {
  if (!chain) return getCreateChainInitialValues();
  return mapApprovalChainToFormValues(chain);
};

export const createChainValidationSchema = Yup.object({
  name: Yup.string().trim().required('Required'),
  type: Yup.string().required('Required'),
  description: Yup.string(),
  flowType: Yup.mixed()
    .required('Required')
    .test('selected', 'Required', (value) => {
      if (value == null || value === '') return false;
      const raw = getRadioOptionValue(value);
      return CHAIN_FLOW_OPTIONS.some((o) => o.label === raw || o.value === raw);
    }),
  approvers: Yup.array()
    .min(1, 'Add at least one approver')
    .required('Add at least one approver'),
});

// —— Hooks (kept in utils per module conventions) ——
export const buildStatusTabs = (activeKey, viewType = VIEW_TYPES.APPROVER) => {
  const tabs = getPageConfig(viewType).statusTabs;
  return tabs.map((tab, index) => ({
    id: index + 1,
    title: tab.label,
    active: tab.id === activeKey,
    key: tab.id,
  }));
};

const getRtkErrorMessage = (error) => {
  if (!error) return null;
  if (typeof error === 'string') return error;
  return (
    error?.data?.message ||
    error?.error ||
    error?.message ||
    'Unable to load approval requests.'
  );
};

export const useAuthorityData = (
  viewType = VIEW_TYPES.APPROVER,
  { typeFilter = 'all', statusTab } = {},
) => {
  const pageConfig = getPageConfig(viewType);
  const requestParams = {
    viewType,
    typeFilter,
    statusTab: statusTab ?? pageConfig.defaultStatusTab,
  };

  const requestsQuery = useGetAuthorityRequestsQuery(requestParams, {
    refetchOnMountOrArgChange: true,
  });
  const statsQuery = useGetAuthorityRequestStatsQuery(viewType, {
    refetchOnMountOrArgChange: true,
  });
  const referenceQuery = useGetAuthorityReferenceDataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const isLoading = requestsQuery.isLoading || referenceQuery.isLoading;

  const isRefetching =
    (requestsQuery.isFetching && !requestsQuery.isLoading) ||
    (referenceQuery.isFetching && !referenceQuery.isLoading);

  const error = useMemo(() => {
    return (
      getRtkErrorMessage(requestsQuery.error) ||
      getRtkErrorMessage(referenceQuery.error)
    );
  }, [requestsQuery.error, referenceQuery.error]);

  const data = useMemo(() => {
    if (!requestsQuery.data && !referenceQuery.data) {
      return null;
    }
    return {
      requests: requestsQuery.data?.requests ?? [],
      departments: referenceQuery.data?.departments ?? [],
      liveJobs: referenceQuery.data?.liveJobs ?? [],
      templates: referenceQuery.data?.templates ?? [],
      approversPool: referenceQuery.data?.approversPool ?? [],
      promotionChannels: referenceQuery.data?.promotionChannels ?? [],
    };
  }, [requestsQuery.data, referenceQuery.data]);

  const refetch = useCallback(async () => {
    try {
      const results = await Promise.all([
        requestsQuery.refetch().catch(() => ({ error: true })),
        statsQuery.refetch().catch(() => ({ error: true })),
        referenceQuery.refetch().catch(() => ({ error: true })),
      ]);
      const failed = results.some((r) => r?.error);
      if (failed) return null;
      return {
        requests: results[0].data?.requests ?? [],
        stats: results[1].data,
        ...results[2].data,
      };
    } catch {
      return null;
    }
  }, [requestsQuery, statsQuery, referenceQuery]);

  const retry = useCallback(() => {
    requestsQuery.refetch();
    statsQuery.refetch();
    referenceQuery.refetch();
  }, [requestsQuery, statsQuery, referenceQuery]);

  return {
    data,
    stats: statsQuery.data,
    isLoading,
    isRefetching,
    error,
    refetch,
    retry,
    hasData: !!data,
  };
};

export const useAuthorityFilters = (
  requests = [],
  viewType = VIEW_TYPES.APPROVER,
  { typeFilter = 'all', statusTab = 'all' } = {},
) => {
  const pageConfig = getPageConfig(viewType);
  const statusTabs = useMemo(() => buildStatusTabs(statusTab, viewType), [statusTab, viewType]);

  return {
    statusTabs,
    filteredRequests: requests,
    pageConfig,
  };
};

export const useNewRequestWizard = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [requestType, setRequestType] = useState(REQUEST_TYPES.HIRE);
  const [currentStep, setCurrentStep] = useState(1);
  const [formKey, setFormKey] = useState(0);

  const steps = useMemo(
    () => (requestType === REQUEST_TYPES.CAMPAIGN ? CAMPAIGN_WIZARD_STEPS : HIRE_WIZARD_STEPS),
    [requestType],
  );

  const stepKey = steps[currentStep - 1]?.key;

  const open = useCallback(() => {
    setIsOpen(true);
    setRequestType(REQUEST_TYPES.HIRE);
    setCurrentStep(1);
    setFormKey((k) => k + 1);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const changeRequestType = useCallback((type) => {
    setRequestType(type);
    setCurrentStep(1);
    setFormKey((k) => k + 1);
  }, []);

  const goNext = useCallback(() => {
    setCurrentStep((s) => Math.min(s + 1, steps.length));
  }, [steps.length]);

  const goBack = useCallback(() => setCurrentStep((s) => Math.max(s - 1, 1)), []);

  const goToStep = useCallback((step) => {
    setCurrentStep((current) => (step >= 1 && step <= current ? step : current));
  }, []);

  return {
    isOpen,
    open,
    close,
    requestType,
    changeRequestType,
    currentStep,
    steps,
    stepKey,
    formKey,
    goNext,
    goBack,
    goToStep,
    isFirstStep: currentStep === 1,
    isLastStep: currentStep === steps.length,
  };
};
